import { useEffect, useMemo, useRef, useState } from 'react';
import type { GeoPoint } from '@runnerboy/types';

export interface LiveMapProps {
  styleUrl: string;
  store?: (GeoPoint & { label?: string }) | null;
  destination?: (GeoPoint & { label?: string }) | null;
  runner?: (GeoPoint & { heading?: number | null }) | null;
  route?: GeoPoint[] | null;
  /** Additional markers (admin live map). */
  points?: { id: string; lat: number; lng: number; kind: 'runner' | 'store' | 'order' | 'problem'; label: string }[];
  className?: string;
  height?: number | string;
  /** Force the schematic renderer (tests / no-WebGL devices). */
  schematic?: boolean;
  /** Space to keep clear when framing the route (e.g. under a floating panel). */
  fitPadding?: number | { top: number; bottom: number; left: number; right: number };
  /** Show zoom buttons (default true). */
  controls?: boolean;
}

type MapLibre = typeof import('maplibre-gl');

const ICONS = {
  store: '<path d="M4 10h16l-1.5-5h-13zM5 10v9h14v-9M9.5 19v-5h5v5" />',
  home: '<path d="M4 11l8-6.5 8 6.5M6.5 9.5V19h11V9.5M10 19v-5h4v5" />',
  scooter: '<circle cx="6" cy="17" r="2.6"/><circle cx="18" cy="17" r="2.6"/><path d="M8.6 17h6.8M14 7h2.5l2.5 7.5M9 11h5l1 6M5 11h4"/>',
};
const glyph = (d: string, color: string, size = 16) =>
  `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;
const PIN = (bg: string, inner: string) => `<div class="rb-marker-pin" style="background:${bg}"><span style="display:grid;place-items:center">${inner}</span></div>`;
const LETTER = (c: string) => `<span style="color:#fff;font:700 13px/1 Inter,system-ui">${c}</span>`;
/** Midnight & Ivory map palette (kept in sync with styles.css tokens). */
const C = { route: '#1d2d70', casing: '#ffffff', store: '#e58a00', dest: '#0e1324', runner: '#0e1324', problem: '#d64545', order: '#2f4bb3', bg: '#f3efe6', grid: '#e6e0d3' };

function hasWebGL(): boolean {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
}

/**
 * Moves the runner marker smoothly from where it is drawn to the newly reported fix. The animation only interpolates
 * between two real reported positions and always ends exactly on the latest one, so the marker never shows a
 * location the runner has not reported.
 */
const glides = new WeakMap<object, number>();
function glide(marker: import('maplibre-gl').Marker, to: GeoPoint, heading: number | null) {
  const from = marker.getLngLat();
  const el = marker.getElement().querySelector<HTMLElement>('.rb-heading');
  if (el) {
    el.style.display = heading === null ? 'none' : '';
    if (heading !== null) el.style.transform = `rotate(${heading}deg)`;
  }
  cancelAnimationFrame(glides.get(marker) ?? 0);
  const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const far = Math.abs(from.lat - to.lat) > 0.02 || Math.abs(from.lng - to.lng) > 0.02;
  if (reduce || far) {
    marker.setLngLat([to.lng, to.lat]);
    return;
  }
  const t0 = performance.now();
  const D = 900;
  const step = (now: number) => {
    const k = Math.min(1, (now - t0) / D);
    const e = 1 - Math.pow(1 - k, 3);
    marker.setLngLat([from.lng + (to.lng - from.lng) * e, from.lat + (to.lat - from.lat) * e]);
    if (k < 1) glides.set(marker, requestAnimationFrame(step));
  };
  glides.set(marker, requestAnimationFrame(step));
}

/**
 * Live delivery map. Uses MapLibre GL with the configured (commercially licensed in production) vector style.
 * Falls back to a dependency-free schematic renderer if WebGL or the tile source is unavailable, so tracking keeps
 * working on low-end devices and poor networks.
 */
export function LiveMap(props: LiveMapProps) {
  const [mode, setMode] = useState<'map' | 'schematic'>(props.schematic ? 'schematic' : 'map');
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<import('maplibre-gl').Map | null>(null);
  const libRef = useRef<MapLibre | null>(null);
  const markers = useRef<Record<string, import('maplibre-gl').Marker>>({});
  const fitted = useRef(false);

  useEffect(() => {
    if (mode !== 'map' || !container.current) return;
    if (!hasWebGL()) {
      setMode('schematic');
      return;
    }
    let cancelled = false;
    const timeout = setTimeout(() => !cancelled && !mapRef.current?.isStyleLoaded() && setMode('schematic'), 8000);
    void import('maplibre-gl').then((mod) => {
      if (cancelled || !container.current) return;
      const lib = (mod as unknown as { default?: MapLibre }).default ?? mod;
      libRef.current = lib;
      const center = props.runner ?? props.destination ?? props.store ?? props.points?.[0] ?? { lat: 20.6, lng: 78.9 };
      const map = new lib.Map({ container: container.current, style: props.styleUrl, center: [center.lng, center.lat], zoom: 13.5, attributionControl: { compact: true } });
      if (props.controls !== false) map.addControl(new lib.NavigationControl({ showCompass: false }), 'top-right');
      map.on('error', () => undefined);
      map.on('load', () => {
        clearTimeout(timeout);
        map.addSource('route', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
        map.addLayer({ id: 'route-casing', type: 'line', source: 'route', paint: { 'line-color': C.casing, 'line-width': 9, 'line-opacity': 0.95 }, layout: { 'line-cap': 'round', 'line-join': 'round' } });
        map.addLayer({ id: 'route', type: 'line', source: 'route', paint: { 'line-color': C.route, 'line-width': 5 }, layout: { 'line-cap': 'round', 'line-join': 'round' } });
      });
      mapRef.current = map;
    });
    return () => {
      cancelled = true;
      clearTimeout(timeout);
      mapRef.current?.remove();
      mapRef.current = null;
      markers.current = {};
      fitted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, props.styleUrl]);

  // Sync markers + route
  useEffect(() => {
    const map = mapRef.current;
    const lib = libRef.current;
    if (mode !== 'map' || !map || !lib) return;
    const want: Record<string, { p: GeoPoint; html: string; z: number }> = {};
    if (props.store) want.store = { p: props.store, html: PIN(C.store, glyph(ICONS.store, '#fff', 15)), z: 1 };
    if (props.destination) want.dest = { p: props.destination, html: PIN(C.dest, glyph(ICONS.home, '#ffaa1f', 15)), z: 1 };
    if (props.runner)
      want.runner = { p: props.runner, html: `<div class="rb-marker-runner" aria-label="Runner"><span class="rb-heading"></span>${glyph(ICONS.scooter, '#ffaa1f', 20)}</div>`, z: 3 };
    for (const pt of props.points ?? []) {
      const color = pt.kind === 'runner' ? C.runner : pt.kind === 'store' ? C.store : pt.kind === 'problem' ? C.problem : C.order;
      want[pt.id] = { p: pt, html: PIN(color, LETTER(pt.kind === 'runner' ? 'R' : pt.kind === 'store' ? 'S' : pt.kind === 'problem' ? '!' : 'O')), z: 2 };
    }
    for (const [id, m] of Object.entries(markers.current)) {
      if (!want[id]) {
        m.remove();
        delete markers.current[id];
      }
    }
    for (const [id, w] of Object.entries(want)) {
      const existing = markers.current[id];
      if (existing && id === 'runner') glide(existing, w.p, props.runner?.heading ?? null);
      else if (existing) existing.setLngLat([w.p.lng, w.p.lat]);
      else {
        const el = document.createElement('div');
        el.innerHTML = w.html;
        el.style.zIndex = String(w.z);
        markers.current[id] = new lib.Marker({ element: el }).setLngLat([w.p.lng, w.p.lat]).addTo(map);
      }
    }
    const src = map.getSource('route') as import('maplibre-gl').GeoJSONSource | undefined;
    if (src && props.route && props.route.length > 1) {
      src.setData({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: props.route.map((p) => [p.lng, p.lat]) } });
    }
    if (!fitted.current) {
      const pts = Object.values(want).map((w) => w.p);
      if (pts.length >= 2) {
        const b = new lib.LngLatBounds();
        pts.forEach((p) => b.extend([p.lng, p.lat]));
        map.fitBounds(b, { padding: props.fitPadding ?? 60, maxZoom: 15.5, duration: 0 });
        fitted.current = true;
      }
    } else if (props.runner) {
      // Keep the whole journey in frame: when the runner leaves the padded visible area (floating controls and
      // panels excluded), re-fit store, runner and destination together instead of only recentring the runner.
      const fp = props.fitPadding ?? 60;
      const pad = typeof fp === 'number' ? { top: fp, bottom: fp, left: fp, right: fp } : fp;
      const pt = map.project([props.runner.lng, props.runner.lat]);
      const box = map.getContainer();
      const inside = pt.x >= pad.left && pt.x <= box.clientWidth - pad.right && pt.y >= pad.top && pt.y <= box.clientHeight - pad.bottom;
      if (!inside) {
        const b = new lib.LngLatBounds();
        Object.values(want).forEach((w) => b.extend([w.p.lng, w.p.lat]));
        map.fitBounds(b, { padding: pad, maxZoom: 15.5, duration: 700 });
      }
    }
  });

  const runnerAttr = props.runner ? { 'data-runner-lat': props.runner.lat.toFixed(6), 'data-runner-lng': props.runner.lng.toFixed(6) } : {};
  return (
    <div className={`rb-map relative overflow-hidden ${props.className ?? ''}`} style={{ height: props.height ?? 280 }} {...runnerAttr} data-map-mode={mode}>
      {mode === 'map' ? <div ref={container} style={{ position: 'absolute', inset: 0 }} /> : <SchematicMap {...props} />}
    </div>
  );
}

/** Dependency-free fallback: equirectangular projection of the relevant points into an SVG. */
export function SchematicMap({ store, destination, runner, route, points }: LiveMapProps) {
  const all = useMemo(() => [store, destination, runner, ...(route ?? []), ...(points ?? [])].filter(Boolean) as GeoPoint[], [store, destination, runner, route, points]);
  if (all.length === 0) return <div className="grid h-full place-items-center bg-paper text-sm text-ink-500">Locating…</div>;
  const lats = all.map((p) => p.lat);
  const lngs = all.map((p) => p.lng);
  const pad = 0.0035;
  const minLat = Math.min(...lats) - pad;
  const maxLat = Math.max(...lats) + pad;
  const minLng = Math.min(...lngs) - pad;
  const maxLng = Math.max(...lngs) + pad;
  const W = 400;
  const H = 280;
  const sx = (lng: number) => ((lng - minLng) / (maxLng - minLng || 1)) * W;
  const sy = (lat: number) => H - ((lat - minLat) / (maxLat - minLat || 1)) * H;
  const path = route && route.length > 1 ? route.map((p, i) => `${i ? 'L' : 'M'}${sx(p.lng).toFixed(1)},${sy(p.lat).toFixed(1)}`).join(' ') : null;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" className="h-full w-full" role="img" aria-label={runner ? `Runner at ${runner.lat.toFixed(4)}, ${runner.lng.toFixed(4)}` : 'Delivery map'}>
      <defs>
        <pattern id="rb-grid" width="28" height="28" patternUnits="userSpaceOnUse">
          <path d="M28 0H0V28" fill="none" stroke={C.grid} strokeWidth="1" />
        </pattern>
      </defs>
      <rect width={W} height={H} fill={C.bg} />
      <rect width={W} height={H} fill="url(#rb-grid)" />
      {path && <path d={path} fill="none" stroke={C.casing} strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" />}
      {path && <path d={path} fill="none" stroke={C.route} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />}
      {(points ?? []).map((p) => (
        <circle key={p.id} cx={sx(p.lng)} cy={sy(p.lat)} r="7" fill={p.kind === 'runner' ? C.runner : p.kind === 'store' ? C.store : p.kind === 'problem' ? C.problem : C.order} stroke="#fff" strokeWidth="2">
          <title>{p.label}</title>
        </circle>
      ))}
      {store && (
        <g transform={`translate(${sx(store.lng)},${sy(store.lat)})`}>
          <circle r="12" fill={C.store} stroke="#fff" strokeWidth="3" />
          <text y="4" textAnchor="middle" fontSize="11" fontWeight="700" fill="#fff">S</text>
        </g>
      )}
      {destination && (
        <g transform={`translate(${sx(destination.lng)},${sy(destination.lat)})`}>
          <circle r="12" fill={C.dest} stroke="#fff" strokeWidth="3" />
          <text y="4" textAnchor="middle" fontSize="11" fontWeight="700" fill="#fff">H</text>
        </g>
      )}
      {runner && (
        <g transform={`translate(${sx(runner.lng)},${sy(runner.lat)})`} style={{ transition: 'transform 900ms linear' }}>
          <circle r="18" fill="#ffaa1f" opacity="0.25">
            <animate attributeName="r" values="10;22;10" dur="1.8s" repeatCount="indefinite" />
          </circle>
          <circle r="13" fill={C.runner} stroke="#fff" strokeWidth="3" />
          <path d="M-5 0h10M1 -4l4 4-4 4" stroke="#ffaa1f" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      )}
    </svg>
  );
}
